import {
  AuthProvider,
  OAuthActionType,
  OAuthErrorResponse,
  OAuthResponse,
  OAuthSuccessResponse,
  UserSetPasswordRequestBody,
  UserSetPasswordResponse,
} from '@fsp/types';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Google as GoogleIcon,
  GppBad as GppBadIcon,
  Save as SaveIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import PaperSection from '../../../components/PaperSection';
import UserAvatar from '../../../components/UserAvatar';
import { useAuth } from '../../../contexts/AuthContext';
import { apiClient } from '../../../services/apiClient';
import PasswordField from '../../auth/components/PasswordField';

const setPasswordSchema = yup.object().shape({});

type SetPasswordFormData = {};

export const SetPassword: React.FC = () => {
  const { user, refreshUserOrganisationState } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [showValidation, setShowValidation] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [verificationToken, setVerificationToken] = useState('');

  // Use ref to track if we've already processed a successful verification
  const hasProcessedVerification = useRef(false);
  const messageHandlerRef = useRef<((event: MessageEvent<OAuthResponse>) => void) | null>(null);

  // Listen for messages from OAuth popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent<OAuthResponse>) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (hasProcessedVerification.current) {
        return;
      }

      const response = event.data;
      if (!response || typeof response.success !== 'boolean') {
        return;
      }

      if (response.success) {
        const successResponse = response as OAuthSuccessResponse;

        if (
          successResponse.action === OAuthActionType.VERIFY &&
          successResponse.verificationToken
        ) {
          // Mark as processed to prevent duplicate handling
          hasProcessedVerification.current = true;

          setIsAuthenticated(true);
          setError('');
          setVerificationToken(successResponse.verificationToken);
          enqueueSnackbar('Authentication successful! You can now set your password.', {
            variant: 'success',
          });
        } else {
          setError('Unexpected response format. Please try again.');
        }
      } else {
        const errorResponse = response as OAuthErrorResponse;
        const errorMessage = errorResponse.message || 'Authentication failed. Please try again.';
        console.log('SetPassword: OAuth error:', errorMessage);
        setError(errorMessage);
        setIsAuthenticated(false);
        setVerificationToken('');
      }
    };

    // Store reference for cleanup
    messageHandlerRef.current = handleMessage;
    window.addEventListener('message', handleMessage);

    return () => {
      if (messageHandlerRef.current) {
        window.removeEventListener('message', messageHandlerRef.current);
        messageHandlerRef.current = null;
      }
    };
  }, [enqueueSnackbar]);

  const { handleSubmit, reset } = useForm<SetPasswordFormData>({
    resolver: yupResolver(setPasswordSchema),
  });

  const handlePasswordValidation = (isValid: boolean) => {
    setIsPasswordValid(isValid);
  };

  const handleAuthProviderLogin = (provider: AuthProvider) => {
    setError(''); // Clear any previous errors
    hasProcessedVerification.current = false; // Reset the processed flag

    const width = 500;
    const height = provider === AuthProvider.GOOGLE ? 600 : 800;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    console.log('SUBMIT FORM');
    const popupUrl = `${process.env.REACT_APP_API_URL}/auth/oauth/${provider}?action=verify&popup=true&userId=${encodeURIComponent(
      user?.id || ''
    )}`;

    console.log('SetPassword: Opening OAuth popup:', popupUrl);

    const popup = window.open(
      popupUrl,
      `${provider}-oauth-verify`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );

    // Check if popup was blocked
    if (!popup || popup.closed) {
      console.log('SetPassword: Popup was blocked or failed to open');
      setError('Please allow popups for this site to authenticate.');
      return;
    }

    console.log('SetPassword: Popup opened successfully');

    // Optional: Monitor popup closure
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        console.log('SetPassword: Popup was closed');
        // Don't set error here as user might have just closed the popup
      }
    }, 1000);
  };

  const onSubmit = async (data: SetPasswordFormData) => {
    if (!isAuthenticated) {
      setError('Please authenticate using one of your connected accounts first');
      return;
    }

    if (!verificationToken) {
      setError('Verification token missing. Please authenticate again.');
      return;
    }

    setError('');
    setIsSubmitting(true);

    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements and passwords match');
      setIsSubmitting(false);
      return;
    }

    try {
      const requestBody: UserSetPasswordRequestBody = {
        newPassword: newPassword,
        verificationToken: verificationToken,
      };

      const response = await apiClient.post('/user/set-password', requestBody);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to set password');
      }

      const responseData: UserSetPasswordResponse = await response.json();

      reset();
      setNewPassword('');
      setConfirmPassword('');
      setIsPasswordValid(false);
      setShowValidation(false);
      setVerificationToken('');
      setIsAuthenticated(false);
      hasProcessedVerification.current = false;
      refreshUserOrganisationState();

      enqueueSnackbar(responseData.message || 'Password added successfully!', {
        variant: 'success',
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to add password. Please try again.';
      setError(errorMessage);
      console.error('Set password error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    reset();
    setNewPassword('');
    setConfirmPassword('');
    setIsPasswordValid(false);
    setError('');
    setVerificationToken('');
    setIsAuthenticated(false);
    hasProcessedVerification.current = false;
  };

  const isDirty = newPassword || confirmPassword;

  const getProviderIcon = (provider: AuthProvider) => {
    switch (provider) {
      case AuthProvider.GOOGLE:
        return <GoogleIcon fontSize="small" />;
      default:
        return <Avatar sx={{ width: 16, height: 16 }}>{provider.charAt(0)}</Avatar>;
    }
  };

  const getProviderLabel = (provider: AuthProvider) => {
    switch (provider) {
      case AuthProvider.GOOGLE:
        return 'Google';
      default:
        return provider;
    }
  };

  const availableProviders = user?.providers?.filter((p) => p !== AuthProvider.PASSWORD) || [];

  if (!user) {
    return <div>Loading...</div>;
  }

  const cardBorderColor = isAuthenticated ? 'success.main' : error ? 'error.main' : 'divider';

  return (
    <PaperSection title="Add Password">
      <Box maxWidth="sm" sx={{ ml: 0, mr: 'auto', textAlign: 'left' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add a password to your account to enable password-based login alongside your connected
          accounts.
        </Typography>

        {/* Current User Profile Card */}
        <Card
          variant="outlined"
          sx={{
            mb: 3,
            border: isAuthenticated ? 2 : 1,
            borderColor: cardBorderColor,
            backgroundColor: isAuthenticated ? 'success.50' : 'background.paper',
          }}
        >
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2}>
              <UserAvatar
                avatarUrl={user?.avatarUrl}
                initials={user?.firstName?.[0] + user?.lastName?.[0]}
                size="medium"
                color="primary.main"
              />
              <Stack spacing={0} flex={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Account Email
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {user?.email}
                </Typography>
              </Stack>
              {isAuthenticated && (
                <Chip
                  icon={<VerifiedUserIcon />}
                  label="Identity Verified"
                  color="success"
                  variant="filled"
                  size="small"
                />
              )}
              {error && !isAuthenticated && (
                <Chip
                  icon={<GppBadIcon />}
                  label="Not Verified"
                  color="error"
                  variant="filled"
                  size="small"
                />
              )}
            </Stack>

            {!isAuthenticated && (
              <>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={2}>
                  <Typography variant="subtitle2" color="text.primary">
                    Verify Your Identity
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Authenticate using one of your connected accounts to add a password:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {availableProviders.map((provider) => (
                      <Button
                        key={provider}
                        variant="outlined"
                        startIcon={getProviderIcon(provider)}
                        onClick={() => handleAuthProviderLogin(provider)}
                        size="small"
                      >
                        {getProviderLabel(provider)}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* New Password */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                Password
              </Typography>
              <PasswordField
                onPasswordChange={setNewPassword}
                onConfirmPasswordChange={setConfirmPassword}
                onValidationChange={handlePasswordValidation}
                showConfirmField={true}
                showValidation={showValidation}
                disabled={isSubmitting || !isAuthenticated}
                margin="none"
              />
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 2 }}>
              {isDirty && (
                <Button variant="outlined" onClick={handleCancel} disabled={isSubmitting}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={
                  isSubmitting ||
                  !isPasswordValid ||
                  !newPassword ||
                  !isAuthenticated ||
                  !verificationToken
                }
                color="primary"
              >
                {isSubmitting ? 'Adding...' : 'Add Password'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Box>
    </PaperSection>
  );
};

export default SetPassword;
